import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Spinner } from "../../../components/Filtering/Reusable/Spinner";
import { useTranslation } from "react-i18next";
import DropDownCountry from "../../../components/buttons/DropDownCountry";
import DropDownDepartment from "../../../components/buttons/DropDownDepartment";
import Dropdown from "../../../components/buttons/Dropdown";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import BreadCrumb from "../../../components/HeaderComponents/BreadCrum";
import {
  toggleSection,
  getControlledList,
  cleanObject,
} from "../../../utils/constants";
import ArrowIcon from "../../../utils/ArrowIcon";
import { useWorkerApiHelper } from "../../../hooks/useWorkerApiHelper";
import { showErrorAlert } from "../../../utils/showErrorAlert";
import { useGetParticipantById } from "../../../hooks/participants/useGetParticipantById";
import { usePutParticipant } from "../../../hooks/participants/usePutParticipants";
import {
  findVocabularyConceptIri,
  resolveControlledCode,
} from "../../../utils/vocabulary";
import MultiLang from "../../../components/MultiLang/MultiLang";
import { availableLanguages } from "../../../i18n";
import {
  addLanguageTab,
  getTextForField,
  removeLanguageTab,
  setTextForField,
} from "../../../utils/formUtils";
import {
  validateEmail,
  validateOpeningHours,
  validatePhone,
} from "../../../utils/utils";
import {
  buildParticipantPayload,
  createInitialParticipantFormData,
  mapParticipantToFormData,
} from "./participantFormData";

function EditParticipant() {
  const { callApiWithWorker } = useWorkerApiHelper();
  const { id } = useParams();
  const [isGeneralOpen, setIsGeneralOpen] = useState(true);
  const [isContactOpen, setIsContactOpen] = useState(true);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [baseLang] = useState(() => i18n.language);
  const [languageTabs, setLanguageTabs] = useState([]);
  const [activeLang, setActiveLang] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [modalMessage] = useState("");
  const {
    data: participantData,
    isLoading: isParticipantLoading,
    isFetching: isParticipantFetching,
  } = useGetParticipantById({
    callApiWithWorker,
    participantId: id,
    lang: i18n.language,
  });
  const showLoadingSpinner =
    loading || isParticipantLoading || isParticipantFetching;

  const updateMut = usePutParticipant({
    callApiWithWorker,
    t,
    onErrorAlert: showErrorAlert,
  });

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const [controlledVocabularies, setControlledVocabularies] = useState({
    organizationType: [],
    countries: [],
    governmentFunctions: [],
    provinces: [],
    postalCodes: [],
  });

  const [formData, setFormData] = useState(() =>
    createInitialParticipantFormData(baseLang),
  );

  const [isFormValid, setIsFormValid] = useState(false);
  const isTranslationMode = activeLang !== "";

  const getPostNameFromPostalCode = (postalCodeValue, options) => {
    const selectedOption = options.find(
      (option) => option.value === postalCodeValue,
    );

    if (!selectedOption?.label) return "";

    const [, ...labelParts] = String(selectedOption.label).split("-");
    return labelParts.join("-").trim();
  };

  const emailValidation = validateEmail(formData.contact_point, t);
  const emailHasError = !emailValidation.valid;
  const phoneValidation = validatePhone(formData.telephone, t);
  const phoneHasError = !phoneValidation.valid;
  const openingHoursValidation = validateOpeningHours(formData.openingHours, t);
  const openingHoursHasError = !openingHoursValidation.valid;

  useEffect(() => {
    if (!participantData) return;

    const next = mapParticipantToFormData(participantData, baseLang);
    setFormData(next);

    const participantNameLangs = Array.isArray(next.participant_name)
      ? next.participant_name
          .map((entry) => entry?.language ?? entry?.lang)
          .filter((lang) => lang && lang !== baseLang)
      : [];

    const descriptionLangs = Array.isArray(next.description)
      ? next.description
          .map((entry) => entry?.language ?? entry?.lang)
          .filter((lang) => lang && lang !== baseLang)
      : [];

    setLanguageTabs([
      ...new Set([...participantNameLangs, ...descriptionLangs]),
    ]);
  }, [participantData, baseLang]);

  useEffect(() => {
    if (!participantData) return;
    if (
      controlledVocabularies.countries.length === 0 ||
      controlledVocabularies.organizationType.length === 0
    ) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      country: findVocabularyConceptIri(
        participantData.address?.adminUnit1,
        controlledVocabularies.countries,
        { lang: i18n.language, fallbackToInput: true },
      ),
      activity_sector: findVocabularyConceptIri(
        participantData.classification?.[0],
        controlledVocabularies.organizationType,
        { lang: i18n.language, fallbackToInput: true },
      ),
      purpose: Array.isArray(participantData.purpose)
        ? participantData.purpose.map(
            (item) =>
              findVocabularyConceptIri(
                item,
                controlledVocabularies.governmentFunctions,
                {
                  lang: i18n.language,
                  fallbackToInput: true,
                },
              ) ?? item,
          )
        : [],
      adminUnit2: findVocabularyConceptIri(
        participantData.address?.adminUnit2,
        controlledVocabularies.provinces,
        { lang: i18n.language, fallbackToInput: true },
      ),
      postCode: resolveControlledCode(
        participantData.address?.postCode,
        controlledVocabularies.postalCodes,
      ),
    }));
  }, [
    participantData,
    controlledVocabularies.countries,
    controlledVocabularies.organizationType,
    controlledVocabularies.governmentFunctions,
    controlledVocabularies.provinces,
    controlledVocabularies.postalCodes,
    i18n.language,
  ]);

  useEffect(() => {
    const requiredFields = ["externalIdentifier"];

    const hasRequiredFields = requiredFields.every((field) => {
      const v = formData[field];
      return typeof v === "string" && v.trim() !== "";
    });

    setIsFormValid(
      hasRequiredFields &&
        validateEmail(formData.contact_point, t).valid &&
        validatePhone(formData.telephone, t).valid &&
        validateOpeningHours(formData.openingHours, t).valid,
    );
  }, [formData]);

  const handleUnifiedChange = (namePathOrEvent, valueOrEvent) => {
    let name, value;

    if (valueOrEvent === undefined) {
      const e = namePathOrEvent;
      name = e.target.name;
      value = e.target.value;
    } else {
      name = namePathOrEvent;
      value = valueOrEvent;
    }

    if (name === "postCode") {
      const nextPostName = getPostNameFromPostalCode(value, postalCodeOptions);
      setFormData((prev) => ({
        ...prev,
        postCode: value,
        postName: nextPostName,
      }));
      return;
    }

    if (Array.isArray(value) && name === "country") {
      value = value[0] ?? "";
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const languageOptions = availableLanguages;
  const translatableFields = ["participant_name", "description"];
  const purposeOptions = controlledVocabularies.governmentFunctions.map(
    (item) => ({
      label: item.label,
      value: item.concept_iri,
    }),
  );
  const provinceOptions = controlledVocabularies.provinces.map((item) => ({
    label: item.label,
    value: item.concept_iri,
  }));
  const postalCodeOptions = controlledVocabularies.postalCodes.map((item) => ({
    label: item.label,
    value: item.code ?? item.concept_iri,
  }));

  const getLangLabel = (code) =>
    languageOptions.find((option) => option.code === code)?.label || code;

  const handleAddLanguageTab = (code) => {
    addLanguageTab(
      code,
      baseLang,
      languageTabs,
      translatableFields,
      formData,
      setFormData,
      setLanguageTabs,
      setActiveLang,
    );
  };

  const handleRemoveLang = (langCode) => {
    removeLanguageTab(
      langCode,
      activeLang,
      translatableFields,
      setFormData,
      setLanguageTabs,
      setActiveLang,
    );
  };

  const renderTranslatableInput = (fieldName, placeholder, isTextarea) => {
    const value = getTextForField(formData, fieldName, activeLang, baseLang);
    const lang = activeLang === "" ? baseLang : activeLang;

    if (isTextarea) {
      return (
        <textarea
          className="inputoffer fontNoto"
          name={fieldName}
          placeholder={placeholder}
          value={value}
          onChange={(e) =>
            setFormData((prev) =>
              setTextForField(prev, fieldName, lang, e.target.value),
            )
          }
        />
      );
    }

    return (
      <input
        className="inputoffer"
        type="text"
        placeholder={placeholder}
        name={fieldName}
        value={value}
        onChange={(e) =>
          setFormData((prev) =>
            setTextForField(prev, fieldName, lang, e.target.value),
          )
        }
      />
    );
  };
  useEffect(() => {
    async function fetchVocabularies() {
      const [
        organizationType,
        countries,
        governmentFunctions,
        provinces,
        postalCodes,
      ] = await Promise.all([
        getControlledList(callApiWithWorker, "organizationtypes-eu", {
          lang: i18n.language,
          connector: "springConnector",
        }),
        getControlledList(callApiWithWorker, "countries", {
          lang: i18n.language,
          connector: "springConnector",
        }),
        getControlledList(callApiWithWorker, "government-functions", {
          lang: i18n.language,
          connector: "springConnector",
        }),
        getControlledList(callApiWithWorker, "provincias", {
          lang: i18n.language,
          connector: "springConnector",
        }),
        getControlledList(callApiWithWorker, "codigos-postales", {
          lang: i18n.language,
          connector: "springConnector",
          size: 16000,
        }),
      ]);

      setControlledVocabularies({
        organizationType,
        countries,
        governmentFunctions,
        provinces,
        postalCodes,
      });
    }
    fetchVocabularies();
  }, [i18n.language]);

  const toggleGeneral = () =>
    toggleSection(setIsGeneralOpen, ".createdivgeneral");
  const toggleContact = () =>
    toggleSection(setIsContactOpen, ".createdivdetails");

  const updateResource = async (e) => {
    e.preventDefault();
    setLoading(true);
    const payload = buildParticipantPayload(formData, baseLang, {
      postalCodeOptions: controlledVocabularies.postalCodes,
      provinceOptions: controlledVocabularies.provinces,
    });

    const cleanedPayload = cleanObject(payload);

    try {
      await updateMut.mutateAsync({
        participantId: id,
        payload: cleanedPayload,
      });

      setLoading(false);
      navigate(-1);
    } catch (error) {
      console.error("Error en la petición:", error);
      setLoading(false);
      showErrorAlert(error);
    }
  };

  return (
    <section className="grisback pb4">
      <div className="whiteback W100">
        <div>
          <BreadCrumb />
        </div>
      </div>
      <div className="containerflex">
        <div className="whiteback W100 padding2rem">
          <div className="w90">
            <h2 className="tittlecatalog">
              {t("participant.editParticipant")}
            </h2>
            <MultiLang
              t={t}
              baseLang={baseLang}
              languageTabs={languageTabs}
              languageOptions={languageOptions}
              activeLang={activeLang}
              addLanguageTab={handleAddLanguageTab}
              removeLanguageTab={handleRemoveLang}
              setActiveLang={setActiveLang}
              getLangLabel={getLangLabel}
            />
          </div>
        </div>
        <div className="W100 containerflex ptb2">
          <div className="w95 bgwhite">
            <div className="creatediv">
              <div
                className="createdivgeneral w100"
                onClick={toggleGeneral}
                style={{ cursor: "pointer" }}
              >
                <h4 className="ptb3lr4">{t("createDataModelDetail.fill")}</h4>
                <ArrowIcon open={isGeneralOpen} />
              </div>
              {isGeneralOpen && (
                <div
                  className="input-fields"
                  style={{ transition: "all 0.3s ease" }}
                >
                  <div className="resource-info">
                    <div className="form-rows">
                      <div className="form-row form-row-two-cols">
                        <div className="form-group">
                          <p className="ptitleinput">{t("participant.name")}</p>
                          {renderTranslatableInput(
                            "participant_name",
                            t("participant.name_placeholder"),
                          )}
                        </div>
                        <div className="form-group">
                          <p className="ptitleinput">
                            {t("dataModelDetail.detail.country")}
                          </p>
                          <DropDownCountry
                            value={formData.country ? [formData.country] : []}
                            onChange={(newCountry) =>
                              handleUnifiedChange(
                                "country",
                                Array.isArray(newCountry)
                                  ? (newCountry[0] ?? "")
                                  : newCountry,
                              )
                            }
                            options={controlledVocabularies.countries.map(
                              (item) => item.label,
                            )}
                            optionsValue={controlledVocabularies.countries.map(
                              (item) => item.concept_iri,
                            )}
                            placeholderText={t("dropdown.countrytwo")}
                            disabled={isTranslationMode}
                          />
                        </div>
                        <div className="form-group">
                          <p className="ptitleinput">
                            {t("participant.nif")}
                            <span className="required">*</span>
                          </p>
                          <input
                            className="inputoffer"
                            type="text"
                            placeholder={t("participant.nif_placeholder")}
                            name="externalIdentifier"
                            value={formData.externalIdentifier}
                            onChange={handleUnifiedChange}
                            disabled={isTranslationMode}
                          />
                        </div>
                      </div>
                      <div className="form-row form-row-two-cols">
                        <div className="form-group">
                          <p className="ptitleinput">
                            {t(
                              "createDataOfferingsDetail.policiesRules.department",
                            )}
                          </p>
                          <DropDownDepartment
                            value={formData.activity_sector}
                            onChange={(departmentValue) =>
                              handleUnifiedChange(
                                "activity_sector",
                                departmentValue,
                              )
                            }
                            options={controlledVocabularies.organizationType.map(
                              (item) => item.label,
                            )}
                            optionsValue={controlledVocabularies.organizationType.map(
                              (item) => item.concept_iri,
                            )}
                            disabled={isTranslationMode}
                          />
                        </div>
                        <div className="form-group">
                          <p className="ptitleinput">
                            {t("participant.purpose")}
                          </p>
                          <Dropdown
                            variant="autocomplete"
                            multiple
                            value={
                              Array.isArray(formData.purpose)
                                ? formData.purpose
                                : []
                            }
                            onChange={(values) =>
                              handleUnifiedChange("purpose", values)
                            }
                            options={purposeOptions}
                            placeholderKey="participant.purpose_placeholder"
                            disabled={isTranslationMode}
                          />
                        </div>
                        <div className="form-group">
                          <p className="ptitleinput">
                            {t("participant.address")}
                          </p>
                          <input
                            className="inputoffer"
                            type="text"
                            placeholder={t("participant.address_placeholder")}
                            name="address"
                            value={formData.address || ""}
                            onChange={handleUnifiedChange}
                            disabled={isTranslationMode}
                          />
                        </div>
                      </div>
                      <div className="form-row form-row-two-cols">
                        <div className="form-group">
                          <p className="ptitleinput">
                            {t("participant.description")}
                          </p>
                          {renderTranslatableInput(
                            "description",
                            t("participant.description_placeholder"),
                            true,
                          )}
                        </div>
                        <div className="form-group">
                          <p className="ptitleinput">
                            {t("participant.email")}
                          </p>
                          <input
                            className="inputoffer"
                            type="email"
                            placeholder={t("participant.email_placeholder")}
                            name="contact_point"
                            value={formData.contact_point}
                            onChange={handleUnifiedChange}
                            disabled={isTranslationMode}
                          />
                          {emailHasError && (
                            <p className="error_text">
                              {emailValidation.error}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="creatediv">
              <div
                className="createdivdetails w100"
                onClick={toggleContact}
                style={{ cursor: "pointer" }}
              >
                <h4 className="ptb3lr4">{t("participant.contactData")}</h4>
                <ArrowIcon open={isContactOpen} />
              </div>
              {isContactOpen && (
                <div
                  className="input-fields"
                  style={{ transition: "all 0.3s ease" }}
                >
                  <div className="resource-info">
                    <div className="form-rows">
                      <div className="form-row-tight participant-contact-row">
                        <div className="form-group">
                          <p className="ptitleinput">
                            {t("participant.adminUnit2")}
                          </p>
                          <Dropdown
                            variant="autocomplete"
                            value={formData.adminUnit2}
                            onChange={(value) =>
                              handleUnifiedChange("adminUnit2", value)
                            }
                            options={provinceOptions}
                            placeholderKey="participant.adminUnit2_placeholder"
                            disabled={isTranslationMode}
                          />
                        </div>
                        <div className="form-group">
                          <p className="ptitleinput">
                            {t("participant.postal")}
                          </p>
                          <Dropdown
                            variant="autocomplete"
                            value={formData.postCode}
                            onChange={(value) =>
                              handleUnifiedChange("postCode", value)
                            }
                            options={postalCodeOptions}
                            placeholderKey="participant.postal_placeholder"
                            disabled={isTranslationMode}
                          />
                        </div>
                        <div className="form-group">
                          <p className="ptitleinput">
                            {t("participant.postalName")}
                          </p>
                          <input
                            className="inputoffer"
                            type="text"
                            name="postName"
                            placeholder={t(
                              "participant.postalName_placeholder",
                            )}
                            value={formData.postName}
                            onChange={handleUnifiedChange}
                            disabled={isTranslationMode}
                          />
                        </div>
                        <div className="form-group">
                          <p className="ptitleinput">
                            {t("participant.phone")}
                          </p>
                          <input
                            className="inputoffer"
                            type="text"
                            name="telephone"
                            placeholder={t("participant.phone_placeholder")}
                            value={formData.telephone}
                            onChange={handleUnifiedChange}
                            disabled={isTranslationMode}
                          />
                          {phoneHasError && (
                            <p className="error_text">{phoneValidation.error}</p>
                          )}
                        </div>
                        <div className="form-group">
                          <p className="ptitleinput">
                            {t("participant.openingHours")}
                          </p>
                          <input
                            className="inputoffer"
                            type="text"
                            name="openingHours"
                            placeholder={t(
                              "participant.openingHours_placeholder",
                            )}
                            value={formData.openingHours}
                            onChange={handleUnifiedChange}
                            disabled={isTranslationMode}
                          />
                          {openingHoursHasError && (
                            <p className="error_text">
                              {openingHoursValidation.error}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="whiteback W100 p2">
        <div className="w90 flexdivbotones">
          <button
            className="boton botoncancel"
            onClick={() => {
              navigate(-1);
            }}
          >
            {t("createDataOfferingsDetail.cancel")}
          </button>
          <button
            type="submit"
            className={`boton botoncapply ${
              !isFormValid ? "disabled-button" : ""
            }`}
            disabled={!isFormValid}
            onClick={updateResource}
          >
            {t("editResource.buttons.update")}
          </button>
        </div>
      </div>
      <div>
        {showLoadingSpinner && (
          <div className="loading">
            <Spinner size="large" color="var(--main-color)" />
          </div>
        )}
      </div>
      <Dialog open={openModal} onClose={handleCloseModal}>
        <DialogTitle>
          {t("createDataModelDetail.messages.information")}
        </DialogTitle>
        <DialogContent>{modalMessage}</DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>OK</Button>
        </DialogActions>
      </Dialog>
    </section>
  );
}

export default EditParticipant;
